/**
 * 应用配置管理模块
 * 统一管理所有API地址和配置项
 */

// 从环境变量读取配置，提供友好的错误提示
function getEnvVar(name, defaultValue = null) {
  const value = process.env[name];
  
  if (!value && defaultValue === null) {
    const env = process.env.NODE_ENV || 'development';
    console.warn(`[配置警告] 环境变量 ${name} 未设置，当前环境: ${env}`);
    console.warn(`请在 .env.${env === 'production' ? 'production' : 'development'} 文件中配置 ${name}`);
  }
  
  return value || defaultValue;
}

// API基础地址配置
const apiBase = getEnvVar('VUE_APP_API_BASE');
const apiBaseRealtime = getEnvVar('VUE_APP_API_BASE_REALTIME');
const lowPerformanceMode = getEnvVar('VUE_APP_LOW_PERFORMANCE_MODE', 'true') === 'true';

// 在开发环境下，如果没有配置则提供明确的错误提示
if (process.env.NODE_ENV !== 'production') {
  if (!apiBase) {
    console.error('[配置错误] VUE_APP_API_BASE 未配置，请检查 .env.development 文件');
  }
  if (!apiBaseRealtime) {
    console.error('[配置错误] VUE_APP_API_BASE_REALTIME 未配置，请检查 .env.development 文件');
  }
}

// API端点路径定义
const apiEndpoints = {
  // 预约相关接口
  appointments: {
    today: (storeId) => `/api/appointment/today/${storeId}`,
    latestByRoom: (storeId) => storeId 
      ? `/api/appointment/latest-by-room/${storeId}`
      : '/api/appointment/latest-by-room'
  },
  // 欢迎语配置相关接口
  welcomeConfig: {
    list: '/api/store-welcome-config/list',
    create: '/api/store-welcome-config',
    update: (id) => `/api/store-welcome-config/${id}`,
    delete: (id) => `/api/store-welcome-config/${id}`,
    enabled: (storeId) => storeId
      ? `/api/store-welcome-config/enabled/${storeId}`
      : '/api/store-welcome-config/enabled',
    toggleEnabled: (id) => `/api/store-welcome-config/${id}/enabled`
  },
  // 实时状态相关接口
  realtime: {
    status: '/api/analysis/realtime/status'
  },
  // 视频管理相关接口
  welcomeVideo: {
    upload: '/api/store-welcome-video/upload',
    get: (id) => `/api/store-welcome-video/${id}`,
    list: '/api/store-welcome-video/list',
    delete: (id) => `/api/store-welcome-video/${id}`,
    store: (storeId) => `/api/store-welcome-video/store/${storeId}`,
    // 单个视频启用/禁用（用于“未启用不在大屏播放”）
    toggleEnabled: (id) => `/api/store-welcome-video/${id}/enabled`
  },
  // 认证相关接口
  auth: {
    login: '/login',
    welcomes: '/api/auth/welcomes'
  },
  // 大屏轮播时长配置
  screenRotationConfig: {
    effective: (storeId) => `/api/screen-rotation-config/effective/${storeId}`,
    list: '/api/screen-rotation-config/list',
    globalGet: '/api/screen-rotation-config/global',
    globalUpsert: '/api/screen-rotation-config/global',
    storeUpsert: (storeId) => `/api/screen-rotation-config/store/${storeId}`,
    storeDelete: (storeId) => `/api/screen-rotation-config/store/${storeId}`
  }
};

/**
 * 构建完整的API URL
 * @param {string} baseUrl - API基础地址
 * @param {string|Function} endpoint - API端点路径或函数
 * @param {...any} params - 端点函数参数
 * @returns {string} 完整的API URL
 */
function buildApiUrl(baseUrl, endpoint, ...params) {
  if (!baseUrl) {
    throw new Error('API基础地址未配置');
  }
  
  const path = typeof endpoint === 'function' 
    ? endpoint(...params)
    : endpoint;
    
  // 确保baseUrl不以/结尾，path以/开头
  const base = baseUrl.replace(/\/$/, '');
  const pathStr = path.startsWith('/') ? path : `/${path}`;
  
  return `${base}${pathStr}`;
}

// 导出配置对象
export default {
  // API基础地址
  apiBase: apiBase || '',
  apiBaseRealtime: apiBaseRealtime || '',
  
  // API端点路径
  apiEndpoints,
  performance: {
    lowPerformanceMode,
    chartUpdateMs: Number(getEnvVar('VUE_APP_CHART_UPDATE_MS', lowPerformanceMode ? '4000' : '2000')),
    brainwaveDisplayMs: Number(getEnvVar('VUE_APP_BRAINWAVE_DISPLAY_MS', lowPerformanceMode ? '2000' : '1000')),
    bubbleUpdateMs: Number(getEnvVar('VUE_APP_BUBBLE_UPDATE_MS', lowPerformanceMode ? '6000' : '3000')),
    waveHistoryLimit: Number(getEnvVar('VUE_APP_WAVE_HISTORY_LIMIT', lowPerformanceMode ? '500' : '1000')),
    waveDisplayPoints: Number(getEnvVar('VUE_APP_WAVE_DISPLAY_POINTS', lowPerformanceMode ? '180' : '333')),
  },
  
  // 构建完整URL的便捷方法
  buildApiUrl,
  
  // 便捷方法：构建主API的完整URL
  getApiUrl(endpoint, ...params) {
    return buildApiUrl(this.apiBase, endpoint, ...params);
  },
  
  // 便捷方法：构建实时API的完整URL
  getRealtimeApiUrl(endpoint, ...params) {
    return buildApiUrl(this.apiBaseRealtime, endpoint, ...params);
  },
  
  // 获取代理目标地址（用于vue.config.js）
  getProxyTarget() {
    return getEnvVar('VUE_APP_PROXY_TARGET', apiBase);
  }
};

